import React, { useMemo, useState, useEffect } from "react";
import { Folder as FolderIcon, ChevronRight, Trash2, FolderPlus, Download, Pencil, Loader2 } from "lucide-react";

/**
 * FolderTree — sidebar tree (iterative pre-order traversal, no recursive JSX).
 *
 * Props:
 *   folders          flat array of {id, name, parent_id, ...}
 *   documents        flat array of {id, folder_id, ...}
 *   currentFolder    "__all__" | "__root__" | folder id
 *   onSelect(id)     selects a folder pseudo-node
 *   onDelete?(id)    admin only; renders trash icon on hover
 *   onAddSubfolder?(parentId)
 *   testIdPrefix     prefix for data-testids
 *   maxDepth         visual safety cap (default 3 to match backend)
 */
export default function FolderTree({
  folders,
  documents,
  currentFolder,
  onSelect,
  onDelete,
  onRename,
  onAddSubfolder,
  onDownload,
  downloadingFolderId = null,
  downloadElapsed = 0,
  testIdPrefix = "folder",
  maxDepth = 3,
}) {
  const childrenByParent = useMemo(() => {
    const map = {};
    folders.forEach((f) => {
      const k = f.parent_id || "__root__";
      (map[k] = map[k] || []).push(f);
    });
    Object.values(map).forEach((arr) =>
      arr.sort(
        (a, b) =>
          (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name)
      )
    );
    return map;
  }, [folders]);

  // Direct child doc count (matches direct-only filtering on the main panel).
  // Sub-folder docs are visible when the user drills into that sub-folder.
  const directDocCount = useMemo(() => {
    const cache = {};
    folders.forEach((f) => {
      cache[f.id] = documents.filter((d) => d.folder_id === f.id).length;
    });
    return cache;
  }, [folders, documents]);

  // Track open/closed state by folder id
  const [openMap, setOpenMap] = useState({});
  const toggleOpen = (id) => setOpenMap((m) => ({ ...m, [id]: !m[id] }));

  // Auto-expand ancestors of currentFolder
  useEffect(() => {
    if (!currentFolder || ["__all__", "__root__"].includes(currentFolder)) return;
    const folderById = Object.fromEntries(folders.map((f) => [f.id, f]));
    const toOpen = {};
    let id = folderById[currentFolder]?.parent_id;
    while (id) {
      toOpen[id] = true;
      id = folderById[id]?.parent_id;
    }
    if (Object.keys(toOpen).length) {
      setOpenMap((m) => ({ ...m, ...toOpen }));
    }
  }, [currentFolder, folders]);

  // Build flat list via iterative pre-order DFS
  const flatRows = useMemo(() => {
    const rows = [];
    const visit = (parentKey, depth) => {
      const kids = childrenByParent[parentKey] || [];
      for (const f of kids) {
        const hasChildren = (childrenByParent[f.id] || []).length > 0;
        rows.push({ folder: f, depth, hasChildren });
        if (openMap[f.id] && hasChildren) {
          visit(f.id, depth + 1);
        }
      }
    };
    visit("__root__", 1);
    return rows;
  }, [childrenByParent, openMap]);

  return (
    <nav className="space-y-1" data-testid="folder-tree">
      <FolderRow
        active={currentFolder === "__all__"}
        onClick={() => onSelect("__all__")}
        label="All documents"
        count={documents.length}
        depth={0}
        testId={`${testIdPrefix}-all`}
      />
      <FolderRow
        active={currentFolder === "__root__"}
        onClick={() => onSelect("__root__")}
        label="Unfiled"
        count={documents.filter((d) => !d.folder_id).length}
        depth={0}
        testId={`${testIdPrefix}-unfiled`}
      />
      {flatRows.map(({ folder, depth, hasChildren }) => (
        <FolderRow
          key={folder.id}
          active={currentFolder === folder.id}
          onClick={() => onSelect(folder.id)}
          label={folder.name}
          count={directDocCount[folder.id] || 0}
          depth={depth}
          hasChildren={hasChildren}
          open={!!openMap[folder.id]}
          onToggle={() => toggleOpen(folder.id)}
          onDelete={onDelete ? () => onDelete(folder.id) : null}
          onRename={onRename ? () => onRename(folder) : null}
          onAddSubfolder={onAddSubfolder && depth < maxDepth ? () => onAddSubfolder(folder.id) : null}
          onDownload={onDownload ? () => onDownload(folder) : null}
          downloading={downloadingFolderId === folder.id}
          downloadElapsed={downloadElapsed}
          testId={`${testIdPrefix}-${folder.id}`}
        />
      ))}
    </nav>
  );
}

function FolderRow({
  active, onClick, label, count, depth = 0, hasChildren = false,
  open = false, onToggle, onDelete, onRename, onAddSubfolder, onDownload,
  downloading = false, downloadElapsed = 0, testId,
}) {
  return (
    <div
      onClick={onClick}
      className={`group flex items-center gap-1.5 pr-2 py-2 rounded-[2px] text-sm transition-colors cursor-pointer ${
        active
          ? "bg-white/5 text-white border-l-2 border-[color:var(--atech-copper)]"
          : "text-[color:var(--atech-text-dim)] hover:text-white hover:bg-white/5 border-l-2 border-transparent"
      }`}
      style={{ paddingLeft: `${10 + depth * 16}px` }}
      data-testid={testId}
    >
      {hasChildren ? (
        <button
          onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
          className="text-[color:var(--atech-text-muted)] hover:text-white shrink-0 -ml-1"
          aria-label={open ? "Collapse" : "Expand"}
          data-testid={`${testId}-toggle`}
        >
          <ChevronRight size={12} className={`transition-transform ${open ? "rotate-90" : ""}`} />
        </button>
      ) : (
        <span className="w-3 shrink-0" />
      )}
      <FolderIcon size={14} className="shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      <span className="text-[10px] text-[color:var(--atech-text-muted)] font-mono">{count}</span>
      {onAddSubfolder && (
        <button
          onClick={(e) => { e.stopPropagation(); onAddSubfolder(); }}
          className="opacity-0 group-hover:opacity-100 text-[color:var(--atech-text-muted)] hover:text-[color:var(--atech-copper)] transition-all"
          title="New sub-folder"
          data-testid={`${testId}-add-sub`}
        >
          <FolderPlus size={12} />
        </button>
      )}
      {onDownload && (
        downloading ? (
          <span
            className="flex items-center gap-1 text-[10px] text-[color:var(--atech-copper)] font-mono shrink-0"
            data-testid={`${testId}-downloading`}
            title="Preparing ZIP…"
          >
            <Loader2 size={12} className="animate-spin" />
            {downloadElapsed}s
          </span>
        ) : (
          <button
            onClick={(e) => { e.stopPropagation(); onDownload(); }}
            className="opacity-0 group-hover:opacity-100 text-[color:var(--atech-text-muted)] hover:text-[color:var(--atech-copper)] transition-all"
            title="Download folder (incl. sub-folders) as zip"
            data-testid={`${testId}-download`}
          >
            <Download size={12} />
          </button>
        )
      )}
      {onRename && (
        <button
          onClick={(e) => { e.stopPropagation(); onRename(); }}
          className="opacity-0 group-hover:opacity-100 text-[color:var(--atech-text-muted)] hover:text-[color:var(--atech-copper)] transition-all"
          title="Rename folder"
          data-testid={`${testId}-rename`}
        >
          <Pencil size={12} />
        </button>
      )}
      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="opacity-0 group-hover:opacity-100 text-[color:var(--atech-text-muted)] hover:text-[#F87171] transition-all"
          title="Delete folder"
          data-testid={`${testId}-delete`}
        >
          <Trash2 size={12} />
        </button>
      )}
    </div>
  );
}

/** FolderBreadcrumb — shows the path from root to currentFolder. */
export function FolderBreadcrumb({ folders, currentFolder, onSelect }) {
  const folderById = useMemo(() => Object.fromEntries(folders.map((f) => [f.id, f])), [folders]);

  if (currentFolder === "__all__") return null;

  const path = [];
  if (currentFolder !== "__root__") {
    let id = currentFolder;
    while (id) {
      const f = folderById[id];
      if (!f) break;
      path.unshift(f);
      id = f.parent_id;
    }
  }

  return (
    <nav className="flex items-center gap-1 text-xs text-[color:var(--atech-text-dim)] flex-wrap mb-3" data-testid="folder-breadcrumb">
      <button
        onClick={() => onSelect("__all__")}
        className="hover:text-white transition-colors"
        data-testid="breadcrumb-all"
      >
        All documents
      </button>
      <ChevronRight size={11} className="text-[color:var(--atech-text-muted)]" />
      <button
        onClick={() => onSelect("__root__")}
        className={`hover:text-white transition-colors ${currentFolder === "__root__" ? "text-white" : ""}`}
        data-testid="breadcrumb-root"
      >
        Unfiled
      </button>
      {path.map((f, i) => (
        <React.Fragment key={f.id}>
          <ChevronRight size={11} className="text-[color:var(--atech-text-muted)]" />
          <button
            onClick={() => onSelect(f.id)}
            className={`hover:text-white transition-colors ${i === path.length - 1 ? "text-white" : ""}`}
            data-testid={`breadcrumb-${f.id}`}
          >
            {f.name}
          </button>
        </React.Fragment>
      ))}
    </nav>
  );
}
